<template>
  <div v-if="!isLoading" class="container__forum-header container__comment">
    <div class="d-flex py-3">
      <base-back-to-previous-button ref="btn__previous-page" :fallback="fallbackRoute" />
    </div>

    <div class="d-flex my-3 flex-row align-items-center">
      <div ref="text__forum-topic" class="text__forum-topic text__topic-title">
        {{ forum.topic }}
      </div>
    </div>

    <div class="d-flex my-3 flex-row">
      <div class="lu-owner-detail">
        <base-user-profile
          ref="image__owner"
          :user-name="userFullName"
          :picture="forumOwner.user.profile_picture"
        ></base-user-profile>

        <div ref="text__owner-info" class="text__owner-detail">
          <span ref="text__owner-fullname" class="text__fullname">{{ userFullName }}</span>
          <span ref="text__owner-label" class="text__role">{{ ownerRole }}</span>
        </div>

        <div ref="text__forum-published-date" class="text__date text-black-50">
          {{ forum.timestamp }}
        </div>
      </div>
    </div>

    <ForumForm
      ref="forum-form__container"
      button-title="Save"
      :reference-meta="referenceMeta"
      :edited-data="editedData"
      :provider="onSave"
      @submit:success="onSubmitSuccess"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
import Forum from '@/models/Forum';
import LearningUnitMember from '@/models/LearningUnitMember';
import UserRole from '@/models/UserRole';
import BaseBackToPreviousButton from '@/components/base-components/BaseBackToPreviousButton';
import BaseUserProfile from '@/components/base-components/BaseUserProfile';
import ForumForm from '@/components/Forum/ForumForm.vue';

export default {
  components: {
    BaseBackToPreviousButton,
    BaseUserProfile,
    ForumForm
  },
  mixins: [pageNavigationMixin],

  props: {
    learningUnitId: {
      type: [Number, String],
      required: true
    },
    forumOwner: {
      type: LearningUnitMember,
      required: true
    },
    forum: {
      type: Forum,
      required: true
    }
  },

  data() {
    return {
      referenceMeta: {
        ref: 'forum',
        refId: this.forum.id,
        field: 'images'
      },
      isLoading: false
    };
  },

  computed: {
    fallbackRoute() {
      const { learningUnitId, forum } = this;

      return {
        name: 'forum',
        params: { learningUnitId, forumId: forum.id }
      };
    },
    userFullName() {
      return `${this.forumOwner.user.firstname} ${this.forumOwner.user.lastname}`;
    },
    ownerRole() {
      const ownerRole = UserRole.learningUnitOwner();

      switch (this.forumOwner.user_role) {
        case ownerRole.id:
          return 'Expert';
        default:
          return null;
      }
    },
    editedData() {
      const { description = '', images = [] } = this.forum;

      return {
        content: description,
        images
      };
    }
  },

  methods: {
    async onSave(body) {
      const { content, images } = body;

      await Forum.api().update(this.forum.id, {
        description: content,
        images: images,
        edited_at: new Date()
      });

      return Forum.find(this.forum.id);
    },

    onSubmitSuccess() {
      this.$toast.success('Forum was updated successfully');
      this.$_pageNavigationMixin_gotoPreviousPage();
    },

    onCancel() {
      this.$_pageNavigationMixin_gotoPreviousPage();
    }
  }
};
</script>

<style lang="scss">
.container__forum-header {
  display: flex;
  flex-direction: column;

  .text__forum-topic {
    color: #3b3e66;
    font-weight: 600;
    font-size: 1.25rem;
  }

  .lu-owner-detail {
    display: flex;
    flex-direction: row;
    gap: 0 0.5rem;
  }

  .text__owner-detail {
    display: flex;
    flex-direction: column;
  }

  .text__owner-detail > .text__fullname {
    color: #3c44b1;
    font-weight: 600;
    font-size: 1rem;
  }

  .text__owner-detail > .text__role {
    color: #7a7b97;
    font-weight: 400;
    font-size: 0.75rem;
  }

  .text__date {
    font-weight: 500;
    font-size: 1rem;
  }
}
</style>
